<template>
  <BaseModal
    :onOpenHook="handleOpen"
    :onCloseHook="handleClose"
    :persistent="loading"
    minHeight="295px"
    modalWidth="600px"
    hookSufix="TwoFactorLoginModal"
    :title="
      mode === 'SELECT_MFA_TYPE'
        ? $t('message.pages.login.2faModal.titleSelectMfaType')
        : $t('message.pages.login.2faModal.title')
    "
    :contentClass="{
      'two-factor-login-modal': true,
      'two-factor-login-modal--select-mfa-type': mode === 'SELECT_MFA_TYPE',
    }"
    :disableDefaultPadding="true"
  >
    <template #subtitle v-if="mode === 'SELECT_MFA_TYPE'">
      <div class="d-flex align-center">
        <span class="body-regular-smaller neutral-40-light--text">
          {{ $t('message.pages.login.2faModal.selectOption') }}
        </span>
        <span
          class="neutral-30-base--text body-bold-smaller ml-1 pointer"
          @click="goToHeaderHelpArticle"
        >
          {{ $t('message.pages.login.2faModal.howToVerify') }}
        </span>
        <v-icon
          size="12"
          class="neutral-30-base--text"
          style="margin-left: 2px"
          @click="goToHeaderHelpArticle"
          >mdi-open-in-new</v-icon
        >
      </div>
    </template>
    <template #content>
      <template v-if="mode === 'SELECT_MFA_TYPE'">
        <div class="select-mfa-type-content">
          <div class="list-container px-6 py-0">
            <v-tooltip bottom :disabled="isSoftwareAvailable">
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-on="on"
                  v-bind="attrs"
                  class="px-4 mfa-list-item"
                  :class="{ pointer: isSoftwareAvailable }"
                  @click="
                    isSoftwareAvailable ? setMode('SOFTWARE_TOKEN_MFA') : null
                  "
                >
                  <div class="content">
                    <img :src="require('@/assets/phone.svg')" class="mr-4" />
                    <div class="d-flex flex-column">
                      <span class="body-bold-small neutral-30-base--text">{{
                        $t('message.pages.login.2faModal.authenticationApp')
                      }}</span>

                      <span
                        class="body-regular-smaller neutral-40-light--text"
                        >{{
                          $t(
                            'message.pages.login.2faModal.useAuthenticationApp'
                          )
                        }}</span
                      >
                    </div>
                  </div>
                  <v-icon class="neutral-20-dark--text" size="16"
                    >mdi-arrow-right</v-icon
                  >
                </div>
              </template>
              <span>
                {{ $t('message.pages.login.2faModal.smsNotAvailable') }}
              </span>
            </v-tooltip>
            <v-tooltip bottom :disabled="isSMSAvailable">
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-on="on"
                  v-bind="attrs"
                  class="px-4 mfa-list-item"
                  :class="{ pointer: isSMSAvailable }"
                  @click="isSMSAvailable ? setMode('SMS_MFA') : null"
                >
                  <div class="content">
                    <img :src="require('@/assets/mail.svg')" class="mr-4" />
                    <div class="d-flex flex-column">
                      <span class="body-bold-small neutral-30-base--text">{{
                        $t('message.pages.login.2faModal.SMS')
                      }}</span>

                      <span
                        class="body-regular-smaller neutral-40-light--text"
                        >{{
                          $t('message.pages.login.2faModal.receiveBySMS')
                        }}</span
                      >
                    </div>
                  </div>
                  <v-icon class="neutral-20-dark--text" size="16"
                    >mdi-arrow-right</v-icon
                  >
                </div>
              </template>
              <span>
                {{ $t('message.pages.login.2faModal.smsNotAvailable') }}
              </span>
            </v-tooltip>
          </div>
        </div>
        <div class="d-flex flex-column px-6 pt-4">
          <span class="body-regular-small neutral-20-dark--text">
            {{ $t('message.pages.login.2faModal.cantAccess') }}
          </span>
          <span class="body-regular-smaller brand-30-base--text">
            {{ $t('message.pages.login.2faModal.contactSupport') }}
          </span>
          <div class="d-flex">
            <span
              class="body-bold-smaller brand-60-base--text pointer"
              @click="goToFooterHelpArticle"
            >
              {{ $t('message.pages.login.2faModal.learnMoreHere') }}
            </span>
            <v-icon
              size="12"
              class="brand-60-base--text"
              style="margin-left: 2px"
              @click="goToFooterHelpArticle"
              >mdi-open-in-new</v-icon
            >
          </div>
        </div>
      </template>
      <v-row no-gutters class="mt-4" v-else>
        <template v-if="mode === 'SOFTWARE_TOKEN_MFA'">
          <v-col cols="12" class="text-center">
            <span class="body-bold-base">
              {{ $t('message.pages.login.2faModal.enterTempCode') }}
            </span>
          </v-col>
          <v-col cols="12" class="text-center">
            <span class="body-regular-small">
              {{ $t('message.pages.login.2faModal.enterSixDigits') }}
            </span>
          </v-col>
          <v-col cols="12" class="text-center mt-6 px-6">
            <v-otp-input
              ref="otpInput"
              v-model="code"
              :length="6"
              @finish="handleVerify"
              :disabled="loading"
              placeholder="0"
              :error="error"
              class="mfa-otp-input"
              type="number"
              focus-all
              autocomplete="one-time-code"
              @focusin="otpRememberIndex"
              @keydown.stop="otpHandleKeydown"
            ></v-otp-input>
          </v-col>
          <v-col cols="12" class="d-flex justify-center">
            <span
              class="body-regular-small feedback-danger--text mt-2"
              v-if="error"
            >
              {{ $t('message.pages.login.2faModal.invalidCode') }}
            </span>
            <LoadingSpinner v-show="loading" class="mt-6" />
          </v-col>
        </template>
        <template v-else-if="mode === 'SMS_MFA'">
          <v-col cols="12" class="text-center">
            <span class="body-bold-base">
              {{ $t('message.pages.login.2faModal.enterSMSCode') }}
              {{ userSmsReceiver.replace('+', '') }}
            </span>
          </v-col>
          <v-col cols="12" class="text-center">
            <span class="body-regular-small">
              {{ $t('message.pages.login.2faModal.enterSixDigits') }}
            </span>
          </v-col>
          <v-col cols="12" class="text-center mt-6 px-6">
            <v-otp-input
              ref="otpInput"
              v-model="code"
              :length="6"
              @finish="handleVerify"
              :disabled="loading"
              placeholder="0"
              :error="error"
              class="mfa-otp-input"
              type="number"
              focus-all
              autocomplete="one-time-code"
              @focusin="otpRememberIndex"
              @keydown.stop="otpHandleKeydown"
            ></v-otp-input>
          </v-col>
          <v-col cols="12" class="d-flex justify-center">
            <span
              class="body-regular-small feedback-danger--text mt-2"
              v-if="error"
            >
              {{ $t('message.pages.login.2faModal.invalidCode') }}
            </span>
            <LoadingSpinner v-show="loading" class="mt-6" />
          </v-col>
          <v-col cols="12" class="text-center mt-6" v-if="!loading && !error">
            <span
              class="body-regular-smaller neutral-40-light--text"
              v-if="smsTimeout > 0"
            >
              {{
                $t('message.pages.login.2faModal.resendCodeIn', {
                  seconds: smsTimeout,
                })
              }}
            </span>
            <base-btn v-else type="secondary" @click="resendSMS">
              {{ $t('message.pages.login.2faModal.resendSMSCode') }}
            </base-btn>
          </v-col>
        </template>
        <v-col cols="12" class="text-center mt-6" v-if="!loading">
          <span
            class="body-bold-small brand-60-base--text pointer"
            @click="goToChangeAuthMethod"
          >
            {{ $t('message.pages.login.2faModal.verifyAnotherWay') }}
          </span>
        </v-col>
      </v-row>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from '../BaseModal.vue'
import LoadingSpinner from '../BaseComponents/LoadingSpinner.vue'
import { mapActions, mapMutations } from 'vuex'
import _ from 'lodash'
import otpInputHandler from '@/utils/TwoFactorAuthMixins/otpInputHandler.mixin.js'

export default {
  name: 'TwoFactorLoginModal',
  mixins: [otpInputHandler],
  components: {
    BaseModal,
    LoadingSpinner,
  },
  data: () => ({
    mode: '',
    code: '',
    hasMultipleMFA: false,
    userSmsReceiver: '',
    user: {},
    loading: false,
    error: false,
    resendSMSRequest: false,
    smsTimeout: 0,
    smsTimeoutInterval: null,
    loadingCallLogin: false,
  }),
  props: {
    otpRef: { default: 'otpInput' },
    otpModelKey: { default: 'code' },
  },
  computed: {
    isSoftwareAvailable() {
      return this.availableMethods.includes('SOFTWARE_TOKEN_MFA')
    },
    isSMSAvailable() {
      return this.availableMethods.includes('SMS_MFA')
    },
    availableMethods() {
      const availableMethods = []

      if (this.user.challengeName === 'SELECT_MFA_TYPE') {
        availableMethods.push('SOFTWARE_TOKEN_MFA')
        availableMethods.push('SMS_MFA')

        return availableMethods
      }

      if (
        this.user.challengeParam.hasOwnProperty(
          'CODE_DELIVERY_DELIVERY_MEDIUM'
        ) ||
        this.user.challengeParam.hasOwnProperty('CODE_DELIVERY_DESTINATION')
      ) {
        availableMethods.push('SMS_MFA')
      }

      if (
        _.isEmpty(this.user.challengeParam) &&
        this.user.challengeName === 'SOFTWARE_TOKEN_MFA'
      ) {
        availableMethods.push('SOFTWARE_TOKEN_MFA')
      }

      return availableMethods
    },
    locale() {
      return this.$i18next.resolvedLanguage
    },
  },
  methods: {
    ...mapActions('session', ['confirmSignIn', 'selectMFA']),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    goToChangeAuthMethod() {
      if (this.loadingCallLogin) return false
      this.loadingCallLogin = true
      this.$root.$emit('callLogin')
    },
    goToHeaderHelpArticle() {
      const lang = this.locale

      const article = {
        pt: 'https://help.pandavideo.com/pt-br/article/como-ativar-e-gerenciar-a-autenticacao-de-dois-fatores-2fa-t33bt6/',
        en: 'https://help.pandavideo.com/en-us/article/how-to-enable-and-manage-two-factor-authentication-2fa-u94hro/',
        es: 'https://help.pandavideo.com/es/article/como-activar-y-gestionar-la-autenticacion-de-dos-factores-2fa-1khjfcf/',
      }

      window.open(article[lang], '_blank')
    },
    goToFooterHelpArticle() {
      const lang = this.locale

      const article = {
        pt: 'https://help.pandavideo.com/pt-br/article/como-ativar-e-gerenciar-a-autenticacao-de-dois-fatores-2fa-t33bt6/#4-o-que-fazer-se-voce-perder-o-acesso-a-autenticacao',
        en: 'https://help.pandavideo.com/en-us/article/how-to-enable-and-manage-two-factor-authentication-2fa-u94hro/#4-what-to-do-if-you-lose-access-to-authentication',
        es: 'https://help.pandavideo.com/es/article/como-activar-y-gestionar-la-autenticacion-de-dos-factores-2fa-1khjfcf/#4-que-hacer-si-pierde-el-acceso-a-la-autenticacion',
      }

      window.open(article[lang], '_blank')
    },
    handleOpen(loginData) {
      this.error = false
      this.code = ''
      this.$root.$on('updateUser', (user) => {
        this.user = user
        this.loadingCallLogin = false
        if (this.resendSMSRequest) {
          this.setMode('SMS_MFA')
          this.setSMSInterval()
          this.resendSMSRequest = false
        } else {
          this.mode = 'SELECT_MFA_TYPE'
        }
      })
      if (loginData.challengeName === 'SELECT_MFA_TYPE') {
        this.hasMultipleMFA = true
      }
      this.userSmsReceiver = loginData.challengeParam.CODE_DELIVERY_DESTINATION
      this.mode = loginData.challengeName
      this.user = loginData
      this.setSMSInterval()
    },
    setSMSInterval() {
      this.smsTimeout = 30
      this.smsTimeoutInterval = setInterval(() => {
        if (this.smsTimeout > 0) {
          this.smsTimeout--
        } else {
          clearInterval(this.smsTimeoutInterval)
        }
      }, 1000)
    },
    handleClose() {
      clearInterval(this.smsTimeoutInterval)
      this.resendSMSRequest = false
      this.loadingCallLogin = false
      this.$root.$off('updateUser')
      this.$emit('close')
    },
    setMode(mode) {
      this.code = ''
      this.mode = mode
      this.error = false
      if (this.hasMultipleMFA) {
        this.handleSetMode()
      } else {
        this.loading = false
      }
    },
    async resendSMS() {
      this.loading = true
      this.resendSMSRequest = true
      this.goToChangeAuthMethod()
      this.handleShowSMSAlert()
    },
    handleShowSMSAlert() {
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: this.$t('message.pages.login.2faModal.codeSentSMS'),
        type: 'success',
      })
    },
    async handleSetMode() {
      try {
        this.error = false
        this.loading = true
        const session = await this.selectMFA({
          user: this.user,
          challengeName: this.mode,
        })
        this.user.Session = session
        this.user.challengeName = this.mode
      } catch (error) {
        console.error('error', error)
        this.error = true
      } finally {
        this.loading = false
        this.focusInput()
      }
    },
    async handleVerify() {
      try {
        this.error = false
        this.loading = true
        await this.confirmSignIn({
          user: this.user,
          challengeAnswer: this.code,
          challengeName: this.mode,
        })
        this.$emit('success')
      } catch (error) {
        console.error('error', error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    focusInput() {
      this.$nextTick(() => {
        setTimeout(() => {
          const otpInput = this.$refs.otpInput
          if (otpInput?.focus) {
            otpInput.focus()
          } else {
            otpInput?.$el.querySelector('input')?.focus()
          }
        }, 100)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.select-mfa-type-content {
  border-top: 1px solid varFigma(neutral-95-lightest);
  border-bottom: 1px solid varFigma(neutral-95-lightest);
}
.list-container {
  .mfa-list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid varFigma(neutral-95-lightest);

    .content {
      display: flex;
    }

    &:last-child {
      border-bottom: none;
    }
  }
}
</style>

<style lang="scss">
.two-factor-login-modal {
  padding-bottom: 24px;
  .modal-header header {
    height: unset !important;
  }
  .v-toolbar__content {
    .spacer {
      display: none;
    }
    padding: 24px !important;
    display: flex;
    justify-content: space-between;
    .v-toolbar__title {
      margin: 0 auto !important;
    }

    .v-toolbar__items {
      i.v-icon {
        color: varFigma(neutral-70) !important;
      }
    }
  }

  .v-otp-input {
    .v-input {
      padding: 0 8px !important;

      .v-input__slot {
        margin: 0 !important;
        background: transparent !important;
      }
    }
  }

  &--select-mfa-type {
    .v-toolbar__content {
      .spacer {
        display: none;
      }
      .v-toolbar__title {
        margin: 0 !important;
      }
      padding: 24px !important;
      display: flex;
      justify-content: space-between;
    }
  }
}

.mfa-otp-input {
  fieldset {
    border: 1px solid varFigma(neutral-95-lightest) !important;
  }
  input {
    color: varFigma(neutral-30-base) !important;

    &::placeholder {
      color: varFigma(neutral-60) !important;
    }
  }
  .error--text {
    fieldset {
      border: 1px solid varFigma(feedback-danger-light) !important;
      background-color: varFigma(feedback-danger-background) !important;
    }

    input {
      color: varFigma(feedback-danger-dark) !important;
    }
  }

  .v-input__control:has(input:focus) {
    fieldset {
      border: 2px solid varFigma(brand-60-base) !important;
    }
    input {
      color: varFigma(neutral-30-base) !important;
    }
  }
}
</style>
